/**
 * Corporate Pipeline — Turn decarbonization initiatives into FI deals
 *
 * Purpose: Volume + Deal Size (not compliance)
 * Takes existing initiatives from CorporateInitiatives and turns
 * them into the FI financing pipeline.
 *
 * Flow:
 *   Corporate Initiative → Select → Submit to FI → Deal Signal → Review
 */
import { useAuth } from "@/context/AuthContext";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/api/client";
import { toast } from "sonner";
import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
import {
  Layers, DollarSign, ArrowRight, CheckCircle2, RefreshCw, Plus,
  Building2, TrendingDown, Target, Zap, ChevronRight, FileText, Info
} from "lucide-react";

const STATUS_CFG = {
  planned:     { color: "bg-slate-100 text-slate-600",      label: "Planned"     },
  in_progress: { color: "bg-blue-100 text-blue-700",        label: "In Progress" },
  completed:   { color: "bg-emerald-100 text-emerald-700",  label: "Completed"   },
};

const CAT_LABELS = {
  renewable_energy:      "Renewable Energy",
  energy_efficiency:     "Energy Efficiency",
  fleet_electrification: "Fleet Electrification",
  waste_reduction:       "Waste Reduction",
  process_optimisation:  "Process Optimisation",
  materials_substitution:"Materials Substitution",
  other:                 "Other",
};

// ── Initiative Card (selectable for FI submission) ─────────────────────────
function InitiativeCard({ initiative, isSelected, onToggle, onSubmit, submitting }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ purpose: "", amount: initiative.capex_usd || "" });
  const statusCfg = STATUS_CFG[initiative.status] || STATUS_CFG.planned;

  const handleSubmit = async () => {
    if (!form.purpose.trim()) { toast.warning("Add a purpose/notes for this financing request."); return; }
    await onSubmit(initiative, form);
    setShowForm(false);
  };

  const vcmLow  = Math.round((initiative.expected_reduction_tco2e || 0) * 15);
  const vcmHigh = Math.round((initiative.expected_reduction_tco2e || 0) * 30);

  return (
    <div className={`bg-white border-2 rounded-xl overflow-hidden transition-all ${
      isSelected ? "border-teal-400 shadow-sm" : "border-slate-200 hover:border-slate-300"
    }`} data-testid={`corporate-initiative-${initiative.id}`}>
      <div className="px-5 py-4 flex items-start gap-3 cursor-pointer" onClick={() => !showForm && onToggle(initiative.id)}>
        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
          isSelected ? "bg-teal-500 border-teal-500" : "border-slate-300"
        }`}>
          {isSelected && <CheckCircle2 size={12} className="text-white" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <p className="text-sm font-bold text-slate-900 truncate">{initiative.name}</p>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${statusCfg.color}`}>{statusCfg.label}</span>
            <span className="text-xs text-slate-400">{CAT_LABELS[initiative.category] || initiative.category}</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-slate-400 flex-wrap">
            {initiative.expected_reduction_tco2e && (
              <span className="text-teal-600 font-semibold">{initiative.expected_reduction_tco2e}t CO₂e/yr</span>
            )}
            {initiative.capex_usd && <span>${initiative.capex_usd.toLocaleString()} CAPEX</span>}
            {vcmLow > 0 && <span className="text-emerald-600">Carbon: ${(vcmLow/1000).toFixed(0)}k–${(vcmHigh/1000).toFixed(0)}k/yr</span>}
          </div>
        </div>
        {isSelected && !showForm && (
          <button
            onClick={e => { e.stopPropagation(); setShowForm(true); }}
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700"
            data-testid={`submit-initiative-${initiative.id}`}>
            <ArrowRight size={12} /> Submit to FI
          </button>
        )}
      </div>

      {/* Inline financing form */}
      {isSelected && showForm && (
        <div className="border-t border-slate-100 px-5 py-4 bg-slate-50/50 space-y-3">
          <p className="text-xs font-bold text-slate-900">Submit to FI Pipeline</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-400 font-semibold mb-1 block">Amount (USD) — from CAPEX</label>
              <input type="number" value={form.amount} onChange={e => setForm(f => ({...f, amount: e.target.value}))}
                className="w-full h-9 px-3 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:border-teal-400 font-mono"
                data-testid={`fi-amount-${initiative.id}`} />
            </div>
            <div>
              <label className="text-xs text-slate-400 font-semibold mb-1 block">Financing Type</label>
              <select className="w-full h-9 px-3 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none">
                <option value="capex_support">Capital Support</option>
                <option value="green_loan">Green Loan</option>
                <option value="blended_finance">Blended Finance</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 font-semibold mb-1 block">Purpose / Use of funds</label>
            <textarea value={form.purpose} onChange={e => setForm(f => ({...f, purpose: e.target.value}))}
              placeholder={`Finance ${initiative.name} — ${initiative.expected_reduction_tco2e || ""}t CO₂e/yr reduction`}
              rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:border-teal-400"
              data-testid={`fi-purpose-${initiative.id}`} />
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleSubmit} disabled={submitting}
              className="flex items-center gap-2 px-4 py-2 bg-[#0F172A] text-white text-xs font-bold rounded-xl hover:bg-slate-800 disabled:opacity-40"
              data-testid={`confirm-submit-${initiative.id}`}>
              {submitting ? <RefreshCw size={12} className="animate-spin" /> : <DollarSign size={12} />}
              {submitting ? "Submitting..." : "Submit to FI Queue"}
            </button>
            <button onClick={() => setShowForm(false)} className="text-xs text-slate-400 hover:text-slate-600">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Submitted Deals Strip ─────────────────────────────────────────────────
function SubmittedDeals({ deals }) {
  if (!deals.length) return null;
  const STATUS_BADGE = {
    submitted:    "bg-blue-100 text-blue-700",
    under_review: "bg-amber-100 text-amber-700",
    qualified:    "bg-emerald-100 text-emerald-700",
    approved:     "bg-green-100 text-green-700",
    declined:     "bg-red-100 text-red-700",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5">
      <p className="text-sm font-semibold text-slate-900 mb-3">Submitted to FI Pipeline</p>
      <div className="space-y-2">
        {deals.map(d => (
          <div key={d.request_id} className="flex items-center gap-3 text-xs">
            <span className="font-mono text-slate-400 text-xs">{d.request_id}</span>
            <span className="flex-1 truncate text-slate-700">{d.purpose}</span>
            {d.amount_requested_usd && <span className="text-slate-500">${d.amount_requested_usd.toLocaleString()}</span>}
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${STATUS_BADGE[d.status] || "bg-slate-100 text-slate-500"}`}>
              {d.status?.replace(/_/g, " ")}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main Pipeline Page ─────────────────────────────────────────────────────
export default function CorporatePipeline() {
  const navigate      = useNavigate();
  const [initiatives, setInitiatives] = useState([]);
  const [submitted,   setSubmitted]   = useState([]);
  const [selected,    setSelected]    = useState(new Set());
  const [loading,     setLoading]     = useState(true);
  const [submitting,  setSubmitting]  = useState(null);
  const ORG_ID = 1; // default — should come from auth context in production

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [iRes, fRes] = await Promise.all([
        client.get(`/corporate/orgs/${ORG_ID}/initiatives`),
        client.get("/finance-requests/?workspace_type=corporate"),
      ]);
      setInitiatives(iRes.data || []);
      setSubmitted((fRes.data?.requests || []).filter(r => r.workspace_type === "corporate"));
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleSelected = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSubmit = async (initiative, form) => {
    setSubmitting(initiative.id);
    try {
      await client.post("/finance-requests/", {
        workspace_type:      "corporate",
        request_type:        "capex_support",
        amount_requested_usd: form.amount ? parseFloat(form.amount) : initiative.capex_usd,
        purpose:             form.purpose || initiative.name,
        applicant_notes:     `Initiative: ${initiative.name} · Reduction: ${initiative.expected_reduction_tco2e}t CO₂e/yr`,
      }).then(async r => {
        await client.post(`/finance-requests/${r.data.request_id}/submit`);
        toast.success(`${r.data.request_id} submitted to FI queue.`);
        setSelected(prev => { const n = new Set(prev); n.delete(initiative.id); return n; });
        load();
      });
    } catch { toast.error("Submission failed."); }
    setSubmitting(null);
  };

  // Pipeline stats
  const activeInitiatives = initiatives.filter(i => i.status !== "completed");
  const totalReduction    = activeInitiatives.reduce((s, i) => s + (i.expected_reduction_tco2e || 0), 0);
  const totalCapex        = activeInitiatives.reduce((s, i) => s + (i.capex_usd || 0), 0);
  const vcmPotentialLow   = Math.round(totalReduction * 15);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <RefreshCw size={18} className="animate-spin text-teal-500" />
    </div>
  );

  return (
    <div className="p-6 lg:p-8 min-h-screen" data-testid="corporate-pipeline">
      {/* Header */}
      <div className="flex items-start justify-between mb-7">
          
      <PageHeader
        title="Decarbonization Pipeline"
        subtitle="Turn your sustainability initiatives into financed climate assets."
      />
        <button onClick={() => navigate("/corporate/initiatives")}
          className="flex items-center gap-2 px-4 py-2.5 border border-slate-200 text-slate-700 text-sm font-semibold rounded-xl hover:bg-slate-50"
          data-testid="manage-initiatives-btn">
          <Plus size={14} /> Manage Initiatives
        </button>
      </div>

      {/* Pipeline summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard label="Active Initiatives"    value={activeInitiatives.length}                                                icon={Layers}      color="text-teal-600"    />
        <MetricCard label="Total CAPEX Required"  value={totalCapex > 0 ? `$${(totalCapex/1000).toFixed(0)}k` : "—"}            icon={DollarSign}  color="text-slate-900"   />
        <MetricCard label="Total CO₂ Reduction"   value={totalReduction > 0 ? `${Math.round(totalReduction)}t/yr` : "—"}         icon={TrendingDown} color="text-emerald-600" />
        <MetricCard label="Carbon Value Potential" value={vcmPotentialLow > 0 ? `$${(vcmPotentialLow/1000).toFixed(0)}k+/yr` : "—"} icon={Zap}       color="text-amber-600"  />
      </div>

      {/* Submitted deals tracker */}
      <SubmittedDeals deals={submitted} />

      {/* Initiative list */}
      {initiatives.length === 0 ? (
        <SectionCard>
          <div className="py-10 text-center">
            <Layers size={32} className="mx-auto text-slate-200 mb-3" />
            <p className="text-sm font-semibold text-slate-500 mb-2">No initiatives yet</p>
            <p className="text-xs text-slate-400 mb-5">Add decarbonization initiatives to build your pipeline.</p>
            <CTAButton variant="action" icon={Plus} onClick={() => navigate("/corporate/initiatives")}>
              Add First Initiative
            </CTAButton>
          </div>
        </SectionCard>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-bold text-slate-900">
              {activeInitiatives.length} active initiative{activeInitiatives.length !== 1 ? "s" : ""}
            </p>
            {selected.size > 0 && (
              <p className="text-xs text-teal-600 font-semibold">{selected.size} selected — click "Submit to FI" on each to send</p>
            )}
          </div>
          <div className="space-y-3">
            {initiatives.map(i => (
              <InitiativeCard
                key={i.id}
                initiative={i}
                isSelected={selected.has(i.id)}
                onToggle={toggleSelected}
                onSubmit={handleSubmit}
                submitting={submitting === i.id}
              />
            ))}
          </div>
        </div>
      )}

      {/* Footer instruction */}
      <div className="mt-6 flex items-start gap-2 text-xs text-slate-400 max-w-xl">
        <Info size={12} className="flex-shrink-0 mt-0.5" />
        <p>
          Select an initiative and click "Submit to FI" to add it to the financing queue.
          The initiative data is frozen at submission — FI sees the state as of now.
          Corporate deals appear in the FI pipeline alongside SME and Project deals.
        </p>
      </div>
    </div>
  );
}
